import { toast } from "react-toastify";

// for show option or hide based on isCollapsibleOption
export function shouldShowOptions({ addon, selectedAddon, quantity }) {
  const numOptions = addon.options?.length || 0;

  if (!addon.isCollapsibleOption && numOptions <= 5) return true;

  if (addon.isCountable) {
    return quantity > 0;
  }

  return selectedAddon?.enabled;
}

export function getAddonQuantity({ addon, selectedAddon, foodItemQuantity, isChecked, isLinked }) {
  // debugger
  if (!addon) return 0;

  if (!selectedAddon) return 0;

  // 1. Checked + Non-countable + Linked → Take food quantity
  if (isChecked && !addon.isCountable && addon.isCountableLinked) {
    return foodItemQuantity;
  }

  // 2. Linked toggle (addon)
  if (isLinked) {
    return selectedAddon?.enabled ? foodItemQuantity : 0;
  }

  // 3. Countable
  if (addon.isCountable) {
    return selectedAddon?.quantity || 0;
  }

  // 4. Non-countable + Non-linked → Sum option quantities
  if (!addon.isCountable && !addon.isCountableLinked) {
    return Object.entries(selectedAddon?.options || {}).reduce((total, [optionId, optionSel]) => {
      if (!optionSel?.enabled) return total;

      const optionDef = (addon.options || []).find(o => o.optionId === optionId);
      if (!optionDef) return total;

      const optionQty = optionDef.isCountableLinked
        ? foodItemQuantity
        : (optionSel.quantity || 1);

      return total + optionQty;
    }, 0);
  }
  return foodItemQuantity;
}

// getOptionQuantity function 
export function getOptionQuantity({ addon, selectedAddon, optionDef, optionSel, foodItemQuantity }) {
  return addon.isCountable && !optionDef.isCountableLinked
    ? selectedAddon.quantity || 0
    : optionDef.isCountableLinked
      ? foodItemQuantity
      : optionDef.isCountable
        ? optionSel.quantity || 0
        : 1;
}

// getOptionPrice function 
export function getOptionPrice({ optionDef, optionQty }) {
  return (optionDef.basePrice || 0) * optionQty;
}

// calculate suboption quantity
export function getSubOptionQuantity({ optionQty }) {
  return optionQty; // Sub-option uses parent option quantity
}

// calculate suboption price
export function getSubOptionPrice({ subOptionDef, subOptionQty }) {
  return (subOptionDef.price || 0) * subOptionQty;
}

export const validateMandatoryAddonsOption = (food, selectedAddons) => {

  // Check for mandatory addons
  const mandatoryAddons = food.addOns.filter((addon) => addon.isMandatory);
  for (const addon of mandatoryAddons) {
    const selectedAddon = selectedAddons[addon.id];
    const isSelected =
      selectedAddon?.enabled || selectedAddon?.quantity > 0 ||
      Object.values(selectedAddon?.options || {}).some((opt) => opt.enabled);
    if (!isSelected) {
      toast.warning(
        `Please select the mandatory addon ${addon.addOnName} to add to cart`
      );
      return;
    }
  }

  // Check mandatory options only if addon is selected
  for (const addon of food.addOns) {
    const selectedAddon = selectedAddons[addon.id] || { enabled: false, quantity: 0, options: {} };

    const isAddonSelected =
      selectedAddon?.enabled ||
      selectedAddon?.quantity > 0 ||
      Object.values(selectedAddon.options).some((opt) => opt.enabled);

    if (!isAddonSelected) {
      continue;
    }

    const mandatoryOptions = addon.options?.filter((option) => option.isMandatory) || [];

    for (const option of mandatoryOptions) {
      const selectedOption = selectedAddon?.options?.[option.optionId];
      const isOptionSelected = selectedOption?.enabled;

      if (!isOptionSelected) {
        toast.warning(
          `Please select the mandatory option ${option.optionName} in ${addon.addOnName} to add to cart`
        );
        return;
      }
    }
  }
  return true;
}

export const validateMustPickedAddonsOption = (food, selectedAddons) => {

  // Step 1: Validate that at least one must-pick add-on is selected if any exist
  const mustPickedAddons = food.addOns.filter((addon) => addon.isMustPick);

  if (mustPickedAddons.length > 0) {
    const isAnyMustPickedAddonSelected = mustPickedAddons.some((addon) => {
      const selectedAddon = selectedAddons[addon.id] || { enabled: false, quantity: 0, options: {} };
      return (
        selectedAddon.enabled ||
        selectedAddon.quantity > 0 ||
        Object.values(selectedAddon.options || {}).some((opt) => opt.enabled)
      );
    });

    if (!isAnyMustPickedAddonSelected) {
      toast.warning(
        `Please select at least one add-on to add to cart.`
      );
      return false;
    }
  }

  // Step 2: Validate that selected add-ons with must-pick options have at least one selected
  for (const addon of food.addOns) {
    const selectedAddon = selectedAddons[addon.id] || { enabled: false, quantity: 0, options: {} };

    // Check if the add-on is selected
    const isAddonSelected =
      selectedAddon.enabled ||
      selectedAddon.quantity > 0 ||
      Object.values(selectedAddon.options || {}).some((opt) => opt.enabled);

    // Skip if add-on is not selected
    if (!isAddonSelected) {
      continue;
    }

    // Check for must-pick options
    const mustPickedOptions = addon.options.filter((option) => option.isOptionMustPick);

    if (mustPickedOptions.length > 0) {
      const isAnyMustPickedOptionSelected = mustPickedOptions.some((option) => {
        const selectedOption = selectedAddon.options?.[option.optionId];
        return selectedOption?.enabled || selectedOption?.quantity > 0;
      });

      if (!isAnyMustPickedOptionSelected) {
        toast.warning(
          `Please select at least one option in "${addon.addOnName}" to add to cart.`
        );
        return false;
      }
    }
  }

  return true;
};

export const validateMinMaxSelection = (food, selectedAddons) => {

  let isValid = true;

  // Check each add-on for min and max selection constraints
  food.addOns.forEach(addOn => {
    const addOnId = addOn.id;
    const selectedAddOn = selectedAddons[addOnId] || { enabled: false, options: {} };

    // Only validate if add-on is selected
    const isAddonSelected =
      selectedAddOn.enabled ||
      selectedAddOn.quantity > 0 ||
      Object.values(selectedAddOn.options).some(opt => opt.enabled);

    if (!isAddonSelected) {
      return;
    }

    const selectedOptionsCount = (selectedAddOn.options && Object.keys(selectedAddOn.options).length)
      ? Object.keys(selectedAddOn.options).filter(optionId => selectedAddOn.options[optionId].enabled).length
      : 0;

    // Trigger validation only if exactly one option is selected
    if (selectedOptionsCount > 0) {
      // Validate minSelection for add-on
      if (addOn.minSelection > 0 && selectedOptionsCount < addOn.minSelection) {
        toast.warning(
          `${addOn.addOnName} requires at least ${addOn.minSelection} option(s) to be selected, but only ${selectedOptionsCount} option(s) are selected.`,
        );
        isValid = false;
      }

      // Validate maxSelection for add-on
      if (addOn.maxSelection > 0 && selectedOptionsCount > addOn.maxSelection) {
        toast.warning(
          `${addOn.addOnName} allows at most ${addOn.maxSelection} option(s), but ${selectedOptionsCount} option(s) are selected.`,
        );
        isValid = false;
      }
    }
  });

  return isValid;
};